import { useState } from "react";
import { Link, Navigate, useLocation, useNavigate } from "react-router";
import { useQuery } from "@tanstack/react-query";
import { Button, Field, Icon, Input } from "@xinghai/ui";
import { ApiError, healthApi } from "../../lib/api";
import { friendlyMessage } from "../../lib/apiErrors";
import { COPYRIGHT_LINE } from "../../lib/constants";
import { homeFor, landingRole } from "../../app/roles";
import { useDesignSystemTheme } from "@xinghai/ui";
import { useTheme } from "../../app/theme";
import { useAuth, useLogin } from "./useAuth";

/** docs/UX.md 公共 —「错误提示不区分用户名/密码错误」。 */
const GENERIC_ERROR = "用户名或密码不对，请再试一次。";
const RATE_LIMITED = "尝试次数过多，请稍后再登录。";

export function LoginPage() {
  const { me, isLoading } = useAuth();
  const login = useLogin();
  const navigate = useNavigate();
  const location = useLocation();
  const { theme } = useTheme();
  useDesignSystemTheme(theme);

  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [remember, setRemember] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // 忘记密码自助只有配了 SMTP 才提供；没配就保留原来的那句话。
  // 这是个公开的部署事实（布尔值），`GET health` 里不含任何凭证。
  const health = useQuery({
    queryKey: ["health"],
    queryFn: ({ signal }) => healthApi.get(signal),
    retry: false,
    staleTime: 5 * 60 * 1000,
  });
  const smtp = health.data?.features?.forgot_password === true;

  if (!isLoading && me) {
    const from = (location.state as { from?: string } | null)?.from;
    return <Navigate to={from ?? homeFor(landingRole(me.user.roles))} replace />;
  }

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    if (username.trim().length === 0 || password.length === 0) {
      setError(GENERIC_ERROR);
      return;
    }
    try {
      const data = await login.mutateAsync({ username: username.trim(), password, remember });
      const from = (location.state as { from?: string } | null)?.from;
      void navigate(from ?? homeFor(landingRole(data.user.roles)), { replace: true });
    } catch (err) {
      // ACCOUNTS.md §3 — never reveal which field was wrong; 429 is the one
      // case worth spelling out, plus a network failure the student can fix.
      if (err instanceof ApiError && err.status === 429) {
        const retry = Number((err.details as { retry_after?: number } | undefined)?.retry_after);
        const minutes = Number.isFinite(retry) ? Math.max(1, Math.ceil(retry / 60)) : null;
        setError(minutes ? `尝试次数过多，请约 ${minutes} 分钟后再登录。` : RATE_LIMITED);
      }
      else if (err instanceof ApiError && err.isNetwork) {
        setError(friendlyMessage(err, GENERIC_ERROR));
      } else setError(GENERIC_ERROR);
    }
  };

  return (
    <main className="xz-login">
      <form className="xz-login__card gz-panel gz-panel--raised" onSubmit={(e) => void onSubmit(e)}>
        <img
          className="xz-login__mark"
          src="/design-system/assets/brand-v2/mark-color.svg"
          alt=""
          aria-hidden="true"
          width={48}
          height={48}
        />
        <h1 className="gz-type-heading">星海正音</h1>
        <p className="gz-muted">用课程发给你的账号登录。</p>

        <Field label="用户名">
          <Input
            name="username"
            value={username}
            autoComplete="username"
            autoCapitalize="off"
            autoCorrect="off"
            onChange={(e) => setUsername(e.target.value)}
          />
        </Field>
        <Field label="密码">
          <Input
            name="password"
            type="password"
            value={password}
            autoComplete="current-password"
            onChange={(e) => setPassword(e.target.value)}
          />
        </Field>

        {error && (
          <p className="gz-notice gz-notice--error" role="alert">
            <Icon id="ui-error" size={20} />
            {error}
          </p>
        )}

        <label className="xz-login__remember">
          <input
            type="checkbox"
            checked={remember}
            onChange={(e) => setRemember(e.target.checked)}
          />
          <span>记住此设备</span>
          <span className="gz-caption">只在你自己的手机或电脑上勾选</span>
        </label>

        <Button type="submit" variant="primary" size="lg" block loading={login.isPending}>
          登录
        </Button>

        <p className="gz-caption">
          还没有账号？学生请<Link to="/register">用班级邀请码注册</Link>。
        </p>
        <p className="gz-caption">
          老师 / 标注专家请<Link to="/invite">输入邀请码</Link>。
        </p>
        {/*
          ACCOUNTS.md §4 — with SMTP configured the student can help themselves;
          without it, this is exactly the sentence that has always been here.
        */}
        {smtp ? (
          <p className="gz-caption">
            忘记密码？<Link to="/forgot">用绑定的邮箱找回</Link>，或联系课程老师获取重置链接。
          </p>
        ) : (
          <p className="gz-caption">忘记密码？联系课程老师获取重置链接。</p>
        )}
        <p className="gz-caption xz-login__org">{COPYRIGHT_LINE}</p>
      </form>
    </main>
  );
}
